class CartDrawerUpsell extends HTMLElement {
  #abortController = null;

  connectedCallback() {
    this.style.display = 'none'; 

    this.slider = this.querySelector('.cart-drawer-upsell__slider');
    this.prevBtn = this.querySelector('.cart-drawer-upsell__arrow--prev');
    this.nextBtn = this.querySelector('.cart-drawer-upsell__arrow--next');

    if (this.slider) {
      this.slider.addEventListener('scroll', this.updateArrows.bind(this), { passive: true });
    }

    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.scrollBy(-1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.scrollBy(1));
    }

    this.cartEvents = ['cart:update', 'cart:updated', 'cart:refresh', 'cart-drawer:open', 'cart-drawer:updated'];
    this.cartEvents.forEach(evt => document.addEventListener(evt, this.#handleCartUpdate));

    // Add a small delay to ensure DOM morphing has settled before fetching
    setTimeout(() => {
      this.fetchRecommendations();
      this.updateArrows();
    }, 250);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    if (this.cartEvents) {
      this.cartEvents.forEach(evt => document.removeEventListener(evt, this.#handleCartUpdate));
    }
  }

  #handleCartUpdate = async (event) => {
    if (event?.detail?.data?.didError) return;

    try {
      const res = await fetch('/cart.js', { cache: 'no-store' });
      const cart = await res.json();

      if (!cart.items || cart.items.length === 0) {
        this.style.display = 'none';
        return;
      }

      const newestItem = cart.items[0]; 
      const newProductId = String(newestItem.product_id);
      const newCartProductIds = cart.items.map(item => String(item.product_id)).join(',');

      // Re-query elements because the cart drawer may have been morphed
      this.slider = this.querySelector('.cart-drawer-upsell__slider');
      this.prevBtn = this.querySelector('.cart-drawer-upsell__arrow--prev');
      this.nextBtn = this.querySelector('.cart-drawer-upsell__arrow--next');

      const isInitialized = this.slider?.dataset.initialized === 'true';
      const productChanged = this.dataset.productId !== newProductId;
      const cartChanged = this.dataset.cartProductIds !== newCartProductIds;

      // Always update the component state first
      this.dataset.productId = newProductId;
      this.dataset.cartProductIds = newCartProductIds;

      if (!isInitialized || productChanged || cartChanged) {
        // Wait for cart drawer DOM morphing to finish
        await new Promise(resolve => setTimeout(resolve, 150));
        await this.fetchRecommendations();
      }
    } catch (err) {
      console.warn('Cart fetch for upsell refresh failed:', err);
    }
  };

  updateArrows() {
    if (!this.slider || !this.prevBtn || !this.nextBtn) return;
    const { scrollLeft, scrollWidth, clientWidth } = this.slider;
    this.prevBtn.disabled = scrollLeft <= 4;
    this.nextBtn.disabled = scrollLeft + clientWidth >= scrollWidth - 4;
  }

  scrollBy(direction) {
    if (!this.slider) return;
    const itemWidth = this.slider.clientWidth;
    this.slider.scrollBy({ left: direction * itemWidth, behavior: 'smooth' });
  }

  getCollectionProducts() {
    const jsonScript = this.querySelector('.cart-drawer-upsell__collection-json');
    if (!jsonScript) return [];
    try {
      return JSON.parse(jsonScript.textContent || '[]');
    } catch (e) {
      console.warn('Failed to parse collection products JSON:', e);
      return [];
    }
  }

  async fetchRecommendations() {
    let productId = this.dataset.productId;
    let intent = this.dataset.intent || 'collection';
    let cartProductIds = (this.dataset.cartProductIds || '').split(',').filter(Boolean);

    // Robust fallback: if productId is missing or invalid
    if (!productId || productId === 'undefined' || productId === 'null') {
      await new Promise(resolve => setTimeout(resolve, 200));
      try {
        const cartRes = await fetch('/cart.js', { cache: 'no-store' });
        const cart = await cartRes.json();
        if (cart.items && cart.items.length > 0) {
          const newestItem = cart.items[0]; 
          productId = String(newestItem.product_id);
          cartProductIds = cart.items.map(item => String(item.product_id));
          this.dataset.productId = productId;
          this.dataset.cartProductIds = cartProductIds.join(',');
        }
      } catch (e) {
        console.warn('Upsell cart fetch failed:', e);
      }
    }

    if (intent === 'collection') {
      const collectionProducts = this.getCollectionProducts();

      const validProducts = collectionProducts.filter(p => !cartProductIds.includes(String(p.id)));

      if (validProducts.length === 0) {
        this.style.display = 'none';
        return;
      }
      this.renderProducts(validProducts);
      return;
    }

    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new AbortController();
    const signal = this.#abortController.signal;

    try {
      let res = await fetch(`/recommendations/products.json?product_id=${productId}&limit=6&intent=${intent}`, { signal });
      if (!res.ok) throw new Error('Recommendations fetch failed');
      let data = await res.json();

      let validProducts = (data.products || []).filter(p => !cartProductIds.includes(String(p.id)));

      if (validProducts.length === 0) {
        const collectionProducts = this.getCollectionProducts();
        validProducts = collectionProducts.filter(p => !cartProductIds.includes(String(p.id)));
        const titleEl = this.querySelector('.cart-drawer-upsell__title');
        if (titleEl) {
          titleEl.textContent = 'You may also like';
        }
      }

      if (validProducts.length === 0) {
        this.style.display = 'none';
        return;
      }

      this.renderProducts(validProducts);
    } catch (e) {
      if (e.name !== 'AbortError') {
        console.warn('Upsell recommendations fetch failed, falling back to collection products:', e);
        const collectionProducts = this.getCollectionProducts();
        const validProducts = collectionProducts.filter(p => !cartProductIds.includes(String(p.id)));
        if (validProducts.length > 0) {
          this.renderProducts(validProducts);
        } else {
          this.style.display = 'none';
        }
      }
    }
  }

  renderProducts(products) {
    // Always re-query slider to survive DOM morphing destruction
    this.slider = this.querySelector('.cart-drawer-upsell__slider');
    // If morph engine stripped it, recreate it
    if (!this.slider) {
      const content = this.querySelector('.cart-drawer-upsell__content');
      if (content) {
        this.slider = document.createElement('div');
        this.slider.className = 'cart-drawer-upsell__slider';
        this.slider.dataset.initialized = 'false';
        this.slider.setAttribute('data-skip-subtree-update', '');
        content.appendChild(this.slider);
      } else {
        return; 
      }
    }

    const style = this.dataset.style || 'list';
    const formatMoney = (cents) => {
      if (typeof cents !== 'number') return '';
      return '$' + (cents / 100).toFixed(2);
    };

    const newSlidesHTML = products.map(product => {
      const variant = product.variants && product.variants[0];
      if (!variant) return '';

      const priceFormatted = formatMoney(product.price);
      const comparePriceFormatted = product.compare_at_price > product.price ? formatMoney(product.compare_at_price) : null;
      const imageSrc = product.featured_image || (product.images && product.images[0]);

      return `
        <div class="cart-drawer-upsell__item" data-product-id="${product.id}">
          <product-card
            class="product-card product-card--${style}"
            data-product-id="${product.id}"
            data-product-variants-size="${product.variants ? product.variants.length : 1}"
          >
            <a
              href="${product.url}"
              class="product-card__link"
              ref="productCardLink"
            >
              <span class="visually-hidden">${product.title.replace(/"/g, '&quot;')}</span>
            </a>

            <div class="product-card__media-wrapper">
              ${imageSrc ? `<img src="${imageSrc}" alt="${product.title.replace(/"/g, '&quot;')}" class="product-card__image" width="${style === 'card' ? '150' : '48'}" height="${style === 'card' ? '150' : '48'}" loading="lazy" />` : ''}
            </div>

            <div class="product-card__details">
              <a href="${product.url}" class="product-card__title">${product.title}</a>
              <div class="product-card__price">
                ${comparePriceFormatted ? `<s class="product-card__price-compare">${comparePriceFormatted}</s>` : ''}
                <span>${priceFormatted}</span>
              </div>
            </div>

            <quick-add-component
              class="quick-add"
              ref="quickAdd"
              data-product-title="${product.title.replace(/"/g, '&quot;')}"
            >
              <product-form-component
                data-section-id="cart-drawer-upsell"
                data-product-id="${product.id}"
                class="quick-add__product-form-component"
              >
                <form method="post" action="/cart/add" data-type="add-to-cart-form" class="shopify-product-form">
                  <input type="hidden" name="id" value="${variant.id}">
                  <input type="hidden" name="quantity" value="1">
                  <button
                    class="button quick-add__button quick-add__button--choose"
                    type="button"
                    name="add"
                    on:click="quick-add-component/handleClick"
                    aria-label="Quick add ${product.title.replace(/"/g, '&quot;')}"
                  >
                    <span class="svg-wrapper add-to-cart-icon">
                      <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 20 20" fill="none" aria-hidden="true"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M16.608 9.421V6.906H3.392v8.016c0 .567.224 1.112.624 1.513.4.402.941.627 1.506.627H8.63M8.818 3h2.333c.618 0 1.212.247 1.649.686a2.35 2.35 0 0 1 .683 1.658v1.562H6.486V5.344c0-.622.246-1.218.683-1.658A2.33 2.33 0 0 1 8.82 3"/><path stroke="currentColor" stroke-linecap="round" stroke-width="var(--icon-stroke-width, 1.5)" d="M14.608 12.563v5m2.5-2.5h-5"/></svg>
                    </span>
                  </button>
                </form>
              </product-form-component>
            </quick-add-component>
          </product-card>
        </div>
      `;
    }).join('');

    this.slider.innerHTML = newSlidesHTML;
    this.slider.dataset.initialized = 'true';
    this.updateArrows();

    // Final safety check: only reveal if products actually injected
    if (this.slider.children.length > 0) {
      this.style.display = 'block';
    } else {
      this.style.display = 'none';
    }
  }
}
if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
